import React, { useMemo } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { selectStylesSm } from '~/Styles/reactSelectStyles';

/**
 * Filtros — 4 combos OPCIONAIS (GE / Praça / Empreendimento / Cobrador) + Consultar + Limpar.
 * Trocar a GE re-busca o combo de Cobrador (RN-14, controlado pelo container). Todos opcionais:
 * Consultar sempre habilitado (data de corte resolvida no backend = ontem 23:59:59).
 *
 * Cidade/Praça tem ~6458 opções — pintar a lista inteira num react-select comum trava a tela
 * (mesma classe do W-0595/W-0949/W-0488). Por isso vai num AsyncSelect com filtro client-side
 * sobre a lista já em memória (opcoesCidade) + teto de resultados (type-to-search). Os demais
 * combos (GE/Empreendimento/Cobrador) são pequenos e seguem como Select comum.
 */
const MIN_BUSCA = 2; // só monta o menu após digitar ao menos 2 chars
const MAX_OPCOES = 50; // teto de itens pintados por busca (evita render de milhares)
const Filtros = ({
  ges = [],
  cidades = [],
  empreendimentos = [],
  cobradores = [],
  geSelecionado,
  cidadeSelecionada,
  empreendimentoSelecionado,
  cobradorSelecionado,
  onChangeGe,
  onChangeCidade,
  onChangeEmpreendimento,
  onChangeCobrador,
  onConsultar,
  onLimpar,
  loading = false,
}) => {
  const opcoesGe = useMemo(
    () =>
      [...ges]
        .map(g => ({ value: g.codigo_grupo_empresa, label: g.apelido }))
        .sort((a, b) => String(a.label).localeCompare(String(b.label))),
    [ges]
  );

  const opcoesCidade = useMemo(
    () =>
      cidades.map(c => ({
        value: c.codigo_cidade,
        label: `${c.descricao_cidade || ''}${c.estado ? ` / ${c.estado}` : ''}`,
      })),
    [cidades]
  );

  // AsyncSelect das Cidades: filtra a lista já em memória (opcoesCidade) por label e limita o
  // número de itens devolvidos. Só busca a partir de MIN_BUSCA chars, então o menu nunca tenta
  // pintar as ~6458 cidades de uma vez (causa do freeze). Roda síncrono via Promise.resolve.
  const carregarCidades = entrada => {
    const termo = (entrada || '').trim().toLowerCase();
    if (termo.length < MIN_BUSCA) return Promise.resolve([]);
    const filtradas = opcoesCidade
      .filter(o => o.label && o.label.toLowerCase().indexOf(termo) !== -1)
      .slice(0, MAX_OPCOES);
    return Promise.resolve(filtradas);
  };

  const opcoesEmpreendimento = useMemo(
    () =>
      empreendimentos.map(e => ({
        value: e.codigo_familia,
        label: e.descricao,
      })),
    [empreendimentos]
  );

  const opcoesCobrador = useMemo(
    () =>
      cobradores.map(c => ({
        value: c.codigo_conta,
        label: c.nome_prestador,
      })),
    [cobradores]
  );

  return (
    <Row className="align-items-end">
      <Col xs="12" md="3">
        <Form.Group>
          <Form.Label>GE</Form.Label>
          <Select
            styles={selectStylesSm}
            options={opcoesGe}
            value={opcoesGe.find(o => o.value === geSelecionado) || null}
            onChange={opt => onChangeGe(opt ? opt.value : null)}
            isClearable
            isDisabled={loading}
            placeholder="Todos"
            noOptionsMessage={() => 'Nenhum GE disponível'}
          />
        </Form.Group>
      </Col>
      <Col xs="12" md="3">
        <Form.Group>
          <Form.Label>Praça / Cidade</Form.Label>
          <AsyncSelect
            styles={selectStylesSm}
            cacheOptions
            defaultOptions={false}
            loadOptions={carregarCidades}
            value={opcoesCidade.find(o => o.value === cidadeSelecionada) || null}
            onChange={opt => onChangeCidade(opt ? opt.value : null)}
            isClearable
            isDisabled={loading}
            placeholder="Digite para buscar..."
            loadingMessage={() => 'Buscando...'}
            noOptionsMessage={({ inputValue }) =>
              inputValue && inputValue.trim().length >= MIN_BUSCA
                ? 'Nenhuma cidade encontrada'
                : 'Digite ao menos 2 caracteres'
            }
          />
        </Form.Group>
      </Col>
      <Col xs="12" md="3">
        <Form.Group>
          <Form.Label>Empreendimento</Form.Label>
          <Select
            styles={selectStylesSm}
            options={opcoesEmpreendimento}
            value={
              opcoesEmpreendimento.find(
                o => o.value === empreendimentoSelecionado
              ) || null
            }
            onChange={opt => onChangeEmpreendimento(opt ? opt.value : null)}
            isClearable
            isDisabled={loading}
            placeholder="Todos"
            noOptionsMessage={() => 'Nenhum empreendimento disponível'}
          />
        </Form.Group>
      </Col>
      <Col xs="12" md="3">
        <Form.Group>
          <Form.Label>Cobrador</Form.Label>
          <Select
            styles={selectStylesSm}
            options={opcoesCobrador}
            value={
              opcoesCobrador.find(o => o.value === cobradorSelecionado) || null
            }
            onChange={opt => onChangeCobrador(opt ? opt.value : null)}
            isClearable
            isDisabled={loading}
            placeholder="Todos"
            noOptionsMessage={() => 'Nenhum cobrador disponível'}
          />
        </Form.Group>
      </Col>
      <Col xs="12" className="d-flex mt-2" style={{ gap: '0.5rem' }}>
        <Button variant="outline-primary" onClick={onConsultar} disabled={loading}>
          <FaSearch /> <span className="ms-1">Consultar</span>
        </Button>
        <Button variant="outline-secondary" onClick={onLimpar} disabled={loading}>
          <FaTimes /> <span className="ms-1">Limpar</span>
        </Button>
      </Col>
    </Row>
  );
};

export default Filtros;
